import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, Send, Filter, CreditCard, Lock, Unlock, CalendarClock, Search } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import MessageFiles from '../../components/MessageFiles';
import LessonFiles from '../../components/LessonFiles';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import PasswordInput from '../../components/PasswordInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function LessonsTab({ ctx }) {
  const {
    token,
    classes,
    users,
    students,
    teachers,
    filteredStudents,
    filteredTeachers,
    newUser,
    setNewUser,
    createStudentDialogOpen,
    setCreateStudentDialogOpen,
    createTeacherDialogOpen,
    setCreateTeacherDialogOpen,
    handleOpenCreateStudent,
    handleOpenCreateTeacher,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleUnlockUser,
    toggleClassSelection,
    studentSearch,
    setStudentSearch,
    teacherSearch,
    setTeacherSearch,
    newClassName,
    setNewClassName,
    classDialogOpen,
    setClassDialogOpen,
    handleCreateClass,
    handleDeleteClass,
    setNewYearWizardOpen,
    lessons,
    lessonClassFilter,
    setLessonClassFilter,
    handleEditLesson,
    handleDeleteLesson,
    boardContent,
    setBoardContent,
    boardClassId,
    setBoardClassId,
    boardFiles,
    setBoardFiles,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    handleSendPrivateMessage,
    recipients,
    boardMessages,
    privateMessages,
    boardFilter,
    setBoardFilter,
    privateFilter,
    setPrivateFilter,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
    payments,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleUpdatePayment,
    paymentStudent,
    setPaymentStudent,
    paymentMonth,
    setPaymentMonth,
    paymentYear,
    setPaymentYear,
    paymentStatus,
    setPaymentStatus,
  } = ctx;
  return (
          <TabsContent value="lessons" data-testid="lessons-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Учебни записи</h2>
                <Select value={lessonClassFilter} onValueChange={setLessonClassFilter}>
                  <SelectTrigger className="w-[140px] sm:w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Филтър по клас" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Всички класове</SelectItem>
                    {classes.map(cls => (
                      <SelectItem key={cls.class_id} value={cls.class_id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {lessons.filter(l => lessonClassFilter === 'all' || l.class_id === lessonClassFilter).length === 0 ? (
                <Card className="p-8 text-center text-slate-500">
                  Няма учебни записи
                </Card>
              ) : (
                <div className="space-y-3">
                  {lessons.filter(l => lessonClassFilter === 'all' || l.class_id === lessonClassFilter).map((lesson) => {
                    const cls = classes.find(c => c.class_id === lesson.class_id);
                    return (
                      <Card key={lesson.lesson_id} data-testid="lesson-card" className="p-4 border border-slate-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs">
                                {new Date(lesson.date).toLocaleDateString('bg-BG')}
                              </Badge>
                              {cls && (
                                <Badge className="text-xs bg-blue-100 text-blue-700">
                                  {cls.name}
                                </Badge>
                              )}
                              <span className="text-xs text-slate-500">Учител: {lesson.teacher_name}</span>
                              {(lesson.file_paths?.length > 0 || lesson.file_path) && (
                                <Badge className="text-xs bg-green-100 text-green-700">С файл</Badge>
                              )}
                            </div>
                            <p className="text-slate-900 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson.description) }} />
                            <YouTubeEmbed html={lesson.description} />
                            <LessonFiles lesson={lesson} token={token} />
                          </div>
                          <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                            <Button
                              onClick={() => handleEditLesson(lesson)}
                              variant="ghost"
                              size="sm"
                              data-testid="edit-lesson-btn"
                              className="text-[#00966E] hover:text-[#007a59] hover:bg-green-50 h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteLesson(lesson.lesson_id)}
                              variant="ghost"
                              size="sm"
                              data-testid="delete-lesson-btn"
                              className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
  );
}
